import { z } from 'zod';
import { deriveScanningMediaMeasurementTotals, deriveScanningMediaRoomQuantities, scanningMediaMillimeterPointSchema, scanningMediaSemanticSnapshotSchema, scanningMediaStableKeySchema, } from './scanning-media-semantic-geometry.contract.js';
export const scanningMediaBoundaryCorrectionCapability = 'scanning-media.boundary-corrections.v1';
const edgeIndexSchema = z.number().int().min(0).max(499);
export const scanningMediaBoundaryOpeningRematchSchema = z.strictObject({
    openingKey: scanningMediaStableKeySchema,
    targetEdgeIndex: edgeIndexSchema,
});
export const scanningMediaBoundaryCorrectionSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaBoundaryCorrectionCapability),
    sourceVersionId: z.string().uuid(),
    floorKey: scanningMediaStableKeySchema,
    roomKey: scanningMediaStableKeySchema,
    floorPolygon: z.array(scanningMediaMillimeterPointSchema).min(3).max(500),
    missingWallEdgeIndexes: z.array(edgeIndexSchema).max(500).default([]),
    openingRematches: z.array(scanningMediaBoundaryOpeningRematchSchema).max(500).default([]),
}).superRefine((value, context) => {
    const missing = new Set();
    for (const [index, edgeIndex] of value.missingWallEdgeIndexes.entries()) {
        if (edgeIndex >= value.floorPolygon.length) {
            context.addIssue({
                code: 'custom',
                path: ['missingWallEdgeIndexes', index],
                message: 'Missing-wall edge indexes must reference the corrected polygon.',
            });
        }
        if (missing.has(edgeIndex)) {
            context.addIssue({
                code: 'custom',
                path: ['missingWallEdgeIndexes', index],
                message: 'Missing-wall edge indexes must be unique.',
            });
        }
        missing.add(edgeIndex);
    }
    const openingKeys = new Set();
    for (const [index, rematch] of value.openingRematches.entries()) {
        if (rematch.targetEdgeIndex >= value.floorPolygon.length) {
            context.addIssue({
                code: 'custom',
                path: ['openingRematches', index, 'targetEdgeIndex'],
                message: 'Opening rematches must reference the corrected polygon.',
            });
        }
        if (openingKeys.has(rematch.openingKey)) {
            context.addIssue({
                code: 'custom',
                path: ['openingRematches', index, 'openingKey'],
                message: 'Each opening may be rematched once.',
            });
        }
        openingKeys.add(rematch.openingKey);
    }
});
export class ScanningMediaBoundaryCorrectionError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ScanningMediaBoundaryCorrectionError';
    }
}
function distance(left, right) {
    return Math.hypot(left.xMm - right.xMm, left.yMm - right.yMm, left.zMm - right.zMm);
}
function wallFitScore(source, target) {
    const direct = (distance(source.start, target.start) + distance(source.end, target.end)) / 2;
    const reversed = (distance(source.start, target.end) + distance(source.end, target.start)) / 2;
    return Math.min(direct, reversed);
}
function correctedWalls(polygon, heightMm) {
    const seenPoints = new Set();
    for (const point of polygon) {
        const key = `${point.xMm}:${point.yMm}:${point.zMm}`;
        if (seenPoints.has(key)) {
            throw new ScanningMediaBoundaryCorrectionError('A corrected boundary cannot repeat a vertex.');
        }
        seenPoints.add(key);
    }
    return polygon.map((start, index) => {
        const end = polygon[(index + 1) % polygon.length];
        if (end === undefined) {
            throw new ScanningMediaBoundaryCorrectionError('The corrected boundary is incomplete.');
        }
        const lengthMm = Math.round(Math.hypot(end.xMm - start.xMm, end.zMm - start.zMm));
        if (!Number.isSafeInteger(lengthMm) || lengthMm <= 0 || lengthMm > 500_000) {
            throw new ScanningMediaBoundaryCorrectionError('Every corrected boundary edge must have a positive bounded length.');
        }
        return {
            key: `boundary-wall:${index}`,
            start,
            end,
            heightMm,
            lengthMm,
        };
    });
}
export function applyScanningMediaBoundaryCorrection(sourceInput, correctionInput, options) {
    const source = scanningMediaSemanticSnapshotSchema.parse(sourceInput);
    const correction = scanningMediaBoundaryCorrectionSchema.parse(correctionInput);
    if (!Number.isSafeInteger(options.openingRematchToleranceMm) ||
        options.openingRematchToleranceMm < 0 ||
        options.openingRematchToleranceMm > 5_000) {
        throw new ScanningMediaBoundaryCorrectionError('The opening rematch tolerance must be an integer from 0 through 5000 millimeters.');
    }
    const sourceFloor = source.floors.find(({ key }) => key === correction.floorKey);
    const sourceRoom = sourceFloor?.rooms.find(({ key }) => key === correction.roomKey);
    if (sourceFloor === undefined || sourceRoom === undefined) {
        throw new ScanningMediaBoundaryCorrectionError('The correction must reference a room in the selected semantic version.');
    }
    const walls = [...correctedWalls(correction.floorPolygon, sourceRoom.ceilingHeightMm)];
    const explicitRematches = new Map(correction.openingRematches.map((rematch) => [rematch.openingKey, rematch.targetEdgeIndex]));
    for (const openingKey of explicitRematches.keys()) {
        if (!sourceRoom.openings.some(({ key }) => key === openingKey)) {
            throw new ScanningMediaBoundaryCorrectionError(`Opening rematch ${openingKey} is not present in the source room.`);
        }
    }
    const rematchedOpenings = sourceRoom.openings.map((opening) => {
        const sourceWall = sourceRoom.walls.find(({ key }) => key === opening.wallKey);
        if (sourceWall === undefined) {
            throw new ScanningMediaBoundaryCorrectionError(`Opening ${opening.key} has no retained source wall.`);
        }
        const explicitIndex = explicitRematches.get(opening.key);
        const candidates = walls
            .map((wall, index) => ({ wall, index, score: wallFitScore(sourceWall, wall) }))
            .filter(({ wall, score }) => wall.lengthMm >= opening.widthMm &&
            wall.heightMm >= opening.sillHeightMm + opening.heightMm &&
            score <= options.openingRematchToleranceMm)
            .sort((left, right) => left.score - right.score || left.index - right.index);
        const target = explicitIndex === undefined
            ? (candidates.length === 1 ? candidates[0] : undefined)
            : walls[explicitIndex] === undefined
                ? undefined
                : { wall: walls[explicitIndex], index: explicitIndex, score: wallFitScore(sourceWall, walls[explicitIndex]) };
        if (target === undefined) {
            throw new ScanningMediaBoundaryCorrectionError(`Opening ${opening.key} cannot be rematched unambiguously; an explicit reviewed edge is required.`);
        }
        if (target.wall.lengthMm < opening.widthMm ||
            target.wall.heightMm < opening.sillHeightMm + opening.heightMm) {
            throw new ScanningMediaBoundaryCorrectionError(`Opening ${opening.key} does not fit the reviewed corrected wall.`);
        }
        return { ...opening, wallKey: target.wall.key };
    });
    const missingWallIndexes = new Set(correction.missingWallEdgeIndexes);
    for (const [index, wall] of walls.entries()) {
        if (missingWallIndexes.has(index) &&
            rematchedOpenings.some(({ wallKey }) => wallKey === wall.key)) {
            throw new ScanningMediaBoundaryCorrectionError(`Corrected edge ${index} already owns an opening and cannot also be a full missing wall.`);
        }
    }
    const missingWallOpenings = walls.flatMap((wall, index) => missingWallIndexes.has(index)
        ? [{
                key: `boundary-passage:${index}`,
                wallKey: wall.key,
                kind: 'passage',
                widthMm: wall.lengthMm,
                heightMm: wall.heightMm,
                sillHeightMm: 0,
            }]
        : []);
    const openings = [...rematchedOpenings, ...missingWallOpenings];
    const correctedRoom = {
        ...sourceRoom,
        floorPolygon: correction.floorPolygon,
        walls,
        openings,
        quantities: deriveScanningMediaRoomQuantities({
            floorPolygon: correction.floorPolygon,
            walls,
            openings,
            ceilingHeightMm: sourceRoom.ceilingHeightMm,
        }),
    };
    const floors = source.floors.map((floor) => floor.key === sourceFloor.key
        ? { ...floor, rooms: floor.rooms.map((room) => room.key === sourceRoom.key ? correctedRoom : room) }
        : floor);
    const snapshot = scanningMediaSemanticSnapshotSchema.parse({
        ...source,
        sourceKind: 'manual',
        floors,
        totals: deriveScanningMediaMeasurementTotals(floors),
    });
    return {
        snapshot,
        rematchedOpeningKeys: rematchedOpenings.map(({ key }) => key),
        missingWallKeys: missingWallOpenings.map(({ wallKey }) => wallKey),
    };
}
//# sourceMappingURL=scanning-media-boundary-correction.contract.js.map